'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import type { PageHeaderProps } from '@routeroyal/theme-core';

/**
 * Stone Investment Page Header
 * 
 * Elegant page header with optional featured image
 */
export function StoneInvestmentPageHeader({ 
  title, 
  subtitle, 
  excerpt,
  featuredImage,
  showBreadcrumb: _showBreadcrumb = false,
  breadcrumbItems: _breadcrumbItems,
  className = '',
}: PageHeaderProps) {
  return (
    <header className={`relative ${className}`}>
      {/* Featured Image Background */}
      {featuredImage?.url && (
        <div className="absolute inset-0 h-[50vh] min-h-[400px]">
          <Image
            src={featuredImage.url}
            alt={title}
            fill
            className="object-cover grayscale"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[var(--color-background)]" />
        </div>
      )}

      <div className={`relative z-10 max-w-4xl mx-auto px-6 lg:px-12 ${featuredImage?.url ? 'pt-48 pb-16' : 'py-20 lg:py-28'}`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 
            className={`text-4xl lg:text-5xl xl:text-6xl tracking-[-0.02em] mb-6 ${
              featuredImage?.url ? 'text-white' : 'text-[var(--color-text)]'
            }`}
            style={{ 
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontWeight: 300,
            }}
          >
            {title}
          </h1>
          
          {subtitle && (
            <p 
              className={`text-xl mb-4 ${
                featuredImage?.url ? 'text-white/80' : 'text-[var(--color-text-muted)]'
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {subtitle}
            </p>
          )}
          
          {excerpt && (
            <p 
              className={`text-base lg:text-lg max-w-2xl ${
                featuredImage?.url ? 'text-white/70' : 'text-[var(--color-text-muted)]'
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {excerpt}
            </p>
          )}
        </motion.div>
      </div>
    </header>
  );
}
